import dayjs from 'dayjs'
import { ComponentProps, FC, useMemo } from 'react'
import { DayPicker, Matcher } from 'react-day-picker'

import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { toMskDayString } from '@/utils/datetime'

import { getSlotPosition, mergeSlots, RowBoundary } from '../lib/slotPosition'

const isWeekRowBoundary = (date: Date | dayjs.Dayjs): RowBoundary => ({
  isRowStart: dayjs(date).day() === 1,
  isRowEnd: dayjs(date).day() === 0
})

const toMskDaySlots = (slots: { from: string; to: string }[] = []) =>
  slots.map(({ from, to }) => ({
    from: toMskDayString(from),
    to: toMskDayString(to)
  }))

type Props = ComponentProps<typeof DayPicker> & {
  individualSlots?: {
    bordered: { from: string; to: string }[]
    disabled: { from: string; to: string }[]
  }
}

const russianFormatters = {
  formatCaption: (date: Date) =>
    new Intl.DateTimeFormat('ru', { month: 'long', year: 'numeric' }).format(
      date
    ),
  formatWeekdayName: (date: Date) => {
    const name = new Intl.DateTimeFormat('ru', { weekday: 'short' }).format(
      date
    )
    return name.charAt(0).toUpperCase() + name.slice(1)
  }
}

const buildSlotModifiers = (
  prefix: string,
  slots: { from: string; to: string }[] = [],
  options: {
    rowAware?: boolean
    excludeFromRounding?: { from: string; to: string }[]
  } = {}
) => {
  const { rowAware = false, excludeFromRounding } = options
  const mergedSlots = mergeSlots(toMskDaySlots(slots), 'day')
  const excludeMergedSlots = excludeFromRounding
    ? mergeSlots(toMskDaySlots(excludeFromRounding), 'day')
    : undefined
  const isRowBoundary = rowAware ? isWeekRowBoundary : undefined

  const hasRoundingConflict = (date: Date) =>
    Boolean(
      excludeMergedSlots &&
        getSlotPosition(date, excludeMergedSlots, 'day') !== null
    )

  return {
    [`${prefix}`]: (date: Date) =>
      getSlotPosition(date, mergedSlots, 'day', isRowBoundary) !== null,
    [`${prefix}Start`]: (date: Date) =>
      getSlotPosition(date, mergedSlots, 'day', isRowBoundary) === 'start' &&
      !hasRoundingConflict(date),
    [`${prefix}End`]: (date: Date) =>
      getSlotPosition(date, mergedSlots, 'day', isRowBoundary) === 'end' &&
      !hasRoundingConflict(date),
    [`${prefix}Middle`]: (date: Date) =>
      getSlotPosition(date, mergedSlots, 'day', isRowBoundary) === 'middle' &&
      !hasRoundingConflict(date),
    [`${prefix}Single`]: (date: Date) =>
      getSlotPosition(date, mergedSlots, 'day', isRowBoundary) === 'single' &&
      !hasRoundingConflict(date)
  }
}

const Calendar: FC<Props> = ({
  className,
  classNames,
  showOutsideDays = true,
  individualSlots,
  disabled,
  modifiers,
  modifiersClassNames,
  ...props
}) => {
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)

  const occupiedSlots = useMemo(
    () =>
      mergeSlots(
        toMskDaySlots([
          ...(individualSlots?.disabled ?? []),
          ...(individualSlots?.bordered ?? [])
        ]),
        'day'
      ),
    [individualSlots?.bordered, individualSlots?.disabled]
  )

  const selectedRange = props.mode === 'range' ? props.selected : undefined
  const selectedFromTime = selectedRange?.from?.getTime()
  const selectedToTime = selectedRange?.to?.getTime()

  const resolvedDisabled = useMemo(() => {
    let disabledArray: Matcher[] = []

    if (Array.isArray(disabled)) {
      disabledArray = disabled
    } else if (disabled) {
      disabledArray = [disabled]
    }

    if (occupiedSlots.length === 0) {
      return disabledArray.length > 0 ? disabledArray : undefined
    }

    // занятую дату нельзя выбрать заново, но если она уже является текущим
    // началом/концом диапазона (пришла извне), не блокируем её - иначе
    // диапазон нельзя будет снять кликом по этой дате
    const isOccupiedAndSelectable = (date: Date) => {
      if (getSlotPosition(date, occupiedSlots, 'day') === null) {
        return false
      }

      const day = dayjs(date).startOf('day')

      return !(
        (Boolean(selectedFromTime) &&
          day.isSame(dayjs(selectedFromTime).startOf('day'))) ||
        (Boolean(selectedToTime) &&
          day.isSame(dayjs(selectedToTime).startOf('day')))
      )
    }

    return [...disabledArray, isOccupiedAndSelectable]
  }, [disabled, occupiedSlots, selectedFromTime, selectedToTime])

  const resolvedModifiers = useMemo(() => {
    const isRangeMiddle = (date: Date) => {
      if (!selectedFromTime || !selectedToTime) {
        return false
      }

      const day = dayjs(date).startOf('day')

      return (
        day.isAfter(dayjs(selectedFromTime).startOf('day')) &&
        day.isBefore(dayjs(selectedToTime).startOf('day'))
      )
    }

    const isRangeEdge = (date: Date) => {
      if (!selectedFromTime && !selectedToTime) {
        return false
      }

      const day = dayjs(date).startOf('day')

      return (
        (Boolean(selectedFromTime) &&
          day.isSame(dayjs(selectedFromTime).startOf('day'))) ||
        (Boolean(selectedToTime) &&
          day.isSame(dayjs(selectedToTime).startOf('day')))
      )
    }

    const isOccupied = (date: Date) =>
      getSlotPosition(date, occupiedSlots, 'day') !== null

    return {
      ...modifiers,
      ...buildSlotModifiers(
        'individualSlotBordered',
        individualSlots?.bordered,
        {
          rowAware: true
        }
      ),
      ...buildSlotModifiers(
        'individualSlotDisabled',
        individualSlots?.disabled,
        {
          excludeFromRounding: individualSlots?.bordered
        }
      ),
      freeRangeMiddle: (date: Date) => isRangeMiddle(date) && !isOccupied(date),
      occupiedRangeMiddle: (date: Date) =>
        isRangeMiddle(date) && isOccupied(date),
      occupiedRangeEdge: (date: Date) => isRangeEdge(date) && isOccupied(date)
    }
  }, [
    individualSlots?.bordered,
    individualSlots?.disabled,
    modifiers,
    occupiedSlots,
    selectedFromTime,
    selectedToTime
  ])

  return (
    <DayPicker
      weekStartsOn={1}
      showOutsideDays={showOutsideDays}
      className={className}
      formatters={russianFormatters}
      disabled={resolvedDisabled}
      modifiers={resolvedModifiers}
      modifiersClassNames={{
        individualSlotBordered: '!border-y !border-bronze-40',
        individualSlotBorderedStart:
          '!border-l !border-r-0 !rounded-0 !rounded-l-6 !rounded-e-0 !rounded-s-6',
        individualSlotBorderedEnd:
          '!border-r !border-l-0 !rounded-0 !rounded-r-6 !rounded-s-0 !rounded-e-6',
        individualSlotBorderedMiddle: '!border-l-0 !border-r-0 !rounded-0',
        individualSlotBorderedSingle: '!border-l !border-r !rounded-6',
        individualSlotDisabled: 'border border-0 bg-bronze-10 !text-grey-100',
        individualSlotDisabledStart:
          '!rounded-0 !rounded-l-6 !rounded-e-0 !rounded-s-6',
        individualSlotDisabledEnd:
          '!rounded-0 !rounded-r-6 !rounded-s-0 !rounded-e-6',
        individualSlotDisabledMiddle: '!rounded-0',
        individualSlotDisabledSingle: '!rounded-6',
        freeRangeMiddle: '!text-grey-80 aria-selected:hover:!text-grey-80',
        occupiedRangeMiddle: '!text-red-70 aria-selected:hover:!text-red-70',
        occupiedRangeEdge: '!text-red-70',
        ...modifiersClassNames
      }}
      classNames={{
        months: 'flex flex-col space-y-4',
        month: 'space-y-4',
        root: 'flex justify-center',
        month_caption:
          'flex justify-center relative items-center text-16 font-bold text-grey-100',
        nav: 'space-x-1 flex items-center',
        nav_button:
          'h-20 w-20 bg-transparent p-0 text-black disabled:text-grey-50',
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'border-collapse',
        weekdays: 'flex',
        weekday: cn(
          'text-grey-80 font-bold text-12 flex w-40 h-40 items-center justify-center',
          isMobileScreen && 'w-52'
        ),
        week: 'flex',
        day: cn(
          'w-40 h-34 aria-selected:hover:!text-white border border-transparent',
          '[&:not(:disabled)]:hover:text-bronze-40 aria-selected:hover:!border-transparent',
          '[&:not(:disabled)]:hover:border-bronze-40',
          isMobileScreen && 'w-52'
        ),
        day_button:
          'font-pro text-center text-16 h-full w-full border border-transparent transition',
        selected: 'bg-bronze-40 text-white rounded-6',
        today: 'text-bronze-40 rounded-6',
        range_start: 'rounded-e-0 rounded-s-6',
        range_end: 'rounded-s-0 rounded-e-6',
        range_middle: '!rounded-0 !bg-grey-10',
        outside: 'day-outside text-grey-70 aria-selected:text-white',
        disabled: '!text-grey-30',
        hidden: 'invisible',
        ...classNames
      }}
      {...props}
    />
  )
}

export default Calendar
